import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import {
  setMobileShowList,
  setSelectedFolder,
  setSelectedProjectId,
  setSelectedShortTermProjectsViewMode,
  setProjectsActivityFilterMode,
  toggleShowAll,
  toggleShowShortTermProjects,
} from 'scenes/Project/store/actions/projectActions';
import { loadProcountorDimensions } from 'scenes/Project/store/thunks/loadProcountorDimensions.thunk';
import { loadProjectByProjectKey } from 'scenes/Project/store/thunks/loadProjectByProjectKey.thunk';
import { loadProjectByProjectKeyAfterJoining } from 'scenes/Project/store/thunks/loadProjectByProjectKeyAfterJoining';
import { saveProcountorDimension } from 'scenes/Project/store/thunks/saveProcountorDimension.thunk';
import { closeShortTermProjectsView } from 'scenes/ShortTermProjects/store/actions/shortTermProjects.actions';
import { loadBusinessShortTermProjects } from 'scenes/ShortTermProjects/store/thunks/loadBusinessShortTermProjects.thunk';
import { loadProjectRecordTotalHours } from 'store/records/thunks/loadProjectRecordTotalHours';
import { loadProjectRecordsFromToday } from 'store/records/thunks/loadProjectRecordsFromToday.thunk';
import { loadResourcesAndResourceEmployersOfProject } from 'store/resources/thunks/loadResourcesAndResourceEmployersOfProject';
import { loadResourcesDataOfProjectSpecificFeature } from 'store/resources/thunks/loadResourcesDataOfProjectSpecificFeature';
import { loadResourcesOfProjectWithEmployerData } from 'store/resources/thunks/loadResourcesOfProjectWithEmployerData';
import { createOrUpdateUser } from 'store/user/thunks/createOrUpdateUser.thunk';
import { clearProjectsOfBusiness } from './actions/projectJoin.actions';
import { unsentFilingsOfProjectChanged } from './actions/verottajailmoitettava.actions';
import {
  setProjectActivityFromDate,
  setProjectActivityToDate,
  setSearchFilter,
  setSelectedStatusBasedProjectsMode,
  setShortTermProjectSearchFilterValue,
} from './actions/projectSearch.actions';
import { addEmployerForUser } from './thunks/addEmployerForUser.thunk';
import { addProjectToMergeFrom } from './thunks/addProjectToMergeFrom.thunk';
import { createProject } from './thunks/createProject.thunk';
import { createShortTermProject } from './thunks/createShortTermProject.thunk';
import { deleteProject } from './thunks/deleteProject.thunk';
import { getProjectMergeStatus } from './thunks/getProjectMergeStatus.thunk';
import { loadAllBusinessIdProjects } from './thunks/loadAllBusinessIdProjects.thunk';
import { loadArchivedProjectsOfBusiness } from './thunks/loadArchivedProjectsOfBusiness.thunk';
import { loadBusinessIntegrationPartners } from './thunks/loadBusinessIntegrationPartners.thunk';
import { loadBusinessIntegrationPartnersForAnyProject } from './thunks/loadBusinessIntegrationPartnersForAnyProject.thunk';
import { loadLatestActiveProjectsOfUser } from './thunks/loadLatestActiveProjectsOfUser.thunk';
import { loadProjectFeatureStatusIndicatorItems } from './thunks/loadProjectStatusFeatureStatusIndicatorItem.thunk';
import { loadProjectsActivityInformationIntimeframe } from './thunks/loadProjectsActivityInformationInTimeframe.thunk';
import {
  VEROTTAJAILMOITETTAVA_SOURCE_KEYS,
  VEROTTAJAILMOITETTAVA_SOURCE_THUNKS,
} from './thunks/loadVerottajailmoitettavaProjectIdsBySource.thunks';
import {
  loadUserProjects,
  loadUserProjectsAndResources,
} from './thunks/project.thunks';
import { putProjectToMergeInto } from './thunks/putProjectToMergeInto.thunk';
import { removeProjectToMergeFrom } from './thunks/removeProjectToMergeFrom.thunk';
import { removeProjectToMergeInto } from './thunks/removeProjectToMergeInto.thunk';
import { saveProjectDashboardLayout } from './thunks/saveProjectDashboardLayout.thunk';
import { startProjectMergeFrom } from './thunks/startProjectMergeFrom.thunk';
import { startProjectMergeInto } from './thunks/startProjectMergeInto.thunk';
import { updateProject } from './thunks/updateProject.thunk';
import { updateProjectDatesFromFilings } from './thunks/updateProjectDatesFromFilings.thunk';
import { updateProjectFromIlve } from './thunks/updateProjectFromIlve.thunk';
import { updateProjectIdCard } from './thunks/updateProjectIdCard.thunk';
import { updateProjectStatus } from './thunks/updateProjectStatus.thunk';
import { getProjectsActivityFilterCacheKey } from './utils/getProjectsActivityFilterCacheKey';
import { userJoinProject } from './thunks/userJoinProject.thunk';
import {
  LATEST_PROJECTS_SOURCE_MODES,
  PROJECTS_ACTIVITY_FILTER_MODES,
  VEROTTAJAILMOITETTAVA_LIST_MODES,
} from './selectors/projectsSelectors';
import { loadUserProjectPreferences } from './thunks/loadUserProjectPreferences.thunk';
import {
  applyOptimisticUserProjectPreferenceUpdate,
  updateUserProjectPreference,
} from './thunks/updateUserProjectPreference.thunk';
import { getUserProjectPreferenceKey } from './utils/userProjectPreferences';

const projectsAdapter = createEntityAdapter();
const projectsOfBusinessAdapter = createEntityAdapter();

const projectEmployeeEmployerProjectsAdapter = createEntityAdapter();

const moveProjectIdToFront = (projectIds = [], projectId) => {
  if (!projectId) {
    return projectIds;
  }

  return [projectId, ...projectIds.filter((id) => id !== projectId)];
};

const initialShortTermProjectsSearchFilters = {
  customer: '',
  address: '',
  name: '',
  orderNumber: '',
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState: {
    projects: projectsAdapter.getInitialState(),
    projectsOfBusiness: projectsOfBusinessAdapter.getInitialState(),
    loading: false,
    businessIdProjectsLoading: false,
    selectedProjectId: '',
    userProjectsLoaded: false,
    projectRecordHours: {},
    projectRecordHoursLoading: false,
    latestUserActiveProjectIdsByEmployer: {},
    latestUserRecordProjectIdsByEmployer: {},
    latestUserActiveProjectIdsRequestStatusByEmployer: {},
    // Source toggles of the two "latest projects" lists: the Projects scene
    // defaults to any activity, the navbar timer drawer to records only.
    latestProjectsListSourceMode: LATEST_PROJECTS_SOURCE_MODES.ANY_ACTIVITY,
    timerDrawerSourceMode: LATEST_PROJECTS_SOURCE_MODES.RECORDS,
    // The tax-filing view's list toggle: all its projects, or only those with
    // unsent filings.
    verottajailmoitettavaListMode: VEROTTAJAILMOITETTAVA_LIST_MODES.ALL,
    verottajailmoitettavaProjectIdsByEmployer: {},
    verottajailmoitettavaProjectIdsRequestStatusByEmployer: {},
    lastProjectClockedInTo: null,
    folder: '',
    showAll: false,
    projectsActivityFilterMode: PROJECTS_ACTIVITY_FILTER_MODES.LATEST,
    showShortTermProjects: false,
    selectedShortTermProjectsViewMode: '',
    mobileShowList: false,
    procountorDimensions: [],
    savingProcountorDimension: false,
    isAddingEmployer: false,
    searchFilter: '',
    isProjectJoined: false,
    projectEmployeeEmployerProjects:
      projectEmployeeEmployerProjectsAdapter.getInitialState(),
    projectMergeStatus: { mergeIntoStatus: undefined, mergeFromStatus: [] },
    projectsMerging: false,
    addingProjectToMerge: false,
    isProjectCreateLoading: false,
    businessIntegrationPartners: {},
    shortTermProjectsSearchFilters: initialShortTermProjectsSearchFilters,
    isLoadingShortTermProjects: false,
    projectToRedirectTo: null,
    isUpdatingProjectStatus: false,
    userProjectPreferences: {},
    userProjectPreferencesLoading: false,
    userProjectPreferencesLoaded: false,
    userProjectPreferenceUpdatesByRequestId: {},
    latestUserProjectPreferenceRequestIdByPreferenceKey: {},
    showHiddenProjects: false,
    showArchivedProjects: false,
    showEndedProjects: false,
    // project list activity filter
    projectActivityData: {},
    projectActivityFromDate: dayjs().startOf('day').toISOString(),
    projectActivityToDate: dayjs().endOf('day').toISOString(),
    isLoadingProjectActivity: false,
    projectFeatureStatusItems: [],
    isLoadingArchivedProjects: false,
    selectedStatusBasedProjectsMode: '',
    projectEditDialogTab: 'project',
  },
  reducers: {
    setShowHiddenProjects: (state, action) => {
      state.showHiddenProjects = Boolean(action.payload);
    },
    toggleShowHiddenProjects: (state) => {
      state.showHiddenProjects = !state.showHiddenProjects;
    },
    setShowArchivedProjects: (state, action) => {
      state.showArchivedProjects = Boolean(action.payload);
    },
    toggleShowArchivedProjects: (state) => {
      state.showArchivedProjects = !state.showArchivedProjects;
    },
    setShowEndedProjects: (state, action) => {
      state.showEndedProjects = Boolean(action.payload);
    },
    setProjectEditDialogTab: (state, action) => {
      state.projectEditDialogTab = action.payload;
    },
    setLatestProjectsListSourceMode: (state, action) => {
      state.latestProjectsListSourceMode = action.payload;
    },
    setVerottajailmoitettavaListMode: (state, action) => {
      state.verottajailmoitettavaListMode = action.payload;
    },
    setTimerDrawerSourceMode: (state, action) => {
      state.timerDrawerSourceMode = action.payload;
    },
    toggleShowEndedProjects: (state) => {
      state.showEndedProjects = !state.showEndedProjects;
    },
    // Optimistically surface a just-opened project at the top of the latest-projects
    // list (keyed by the active employer's cache key) so it appears instantly without
    // waiting for a refetch of the 30-day activity list. A later fetch reconciles it.
    addProjectToLatestActiveProjects: (state, action) => {
      const { activeEmployerInfo, projectId } = action.payload || {};
      if (!projectId) {
        return;
      }
      const cacheKey = getProjectsActivityFilterCacheKey(activeEmployerInfo);
      if (!cacheKey) {
        return;
      }
      state.latestUserActiveProjectIdsByEmployer[cacheKey] = moveProjectIdToFront(
        state.latestUserActiveProjectIdsByEmployer[cacheKey] || [],
        projectId
      );
    },
  },
  extraReducers(builder) {
    const setVerottajailmoitettavaSourceStatus = (
      state,
      cacheKey,
      sourceKey,
      status
    ) => {
      if (!cacheKey || !sourceKey) {
        return;
      }

      if (
        !state.verottajailmoitettavaProjectIdsRequestStatusByEmployer[cacheKey]
      ) {
        state.verottajailmoitettavaProjectIdsRequestStatusByEmployer[cacheKey] =
          {};
      }

      state.verottajailmoitettavaProjectIdsRequestStatusByEmployer[cacheKey][
        sourceKey
      ] = status;
    };

    const getVerottajailmoitettavaSourceStatus = (state, cacheKey, sourceKey) =>
      state.verottajailmoitettavaProjectIdsRequestStatusByEmployer[cacheKey]?.[
        sourceKey
      ];

    VEROTTAJAILMOITETTAVA_SOURCE_THUNKS.forEach((thunk) => {
      const sourceKey = thunk.typePrefix.split('/').pop();

      builder
        .addCase(thunk.pending, (state, action) => {
          const cacheKey = getProjectsActivityFilterCacheKey(
            action.meta.arg?.activeEmployerInfo
          );

          setVerottajailmoitettavaSourceStatus(
            state,
            cacheKey,
            sourceKey,
            'pending'
          );
        })
        .addCase(thunk.fulfilled, (state, action) => {
          const { cacheKey, projectIds } = action.payload;

          if (!cacheKey) {
            return;
          }

          if (!state.verottajailmoitettavaProjectIdsByEmployer[cacheKey]) {
            state.verottajailmoitettavaProjectIdsByEmployer[cacheKey] = {};
          }

          state.verottajailmoitettavaProjectIdsByEmployer[cacheKey][sourceKey] =
            projectIds;

          // Went stale while this answer was on its way (a filing changed, see
          // unsentFilingsOfProjectChanged): the answer can predate the change,
          // so it stays stale for the next load to ask again.
          const wentStale =
            getVerottajailmoitettavaSourceStatus(state, cacheKey, sourceKey) ===
            'stale';

          setVerottajailmoitettavaSourceStatus(
            state,
            cacheKey,
            sourceKey,
            wentStale ? 'stale' : 'fulfilled'
          );
        })
        .addCase(thunk.rejected, (state, action) => {
          const cacheKey = getProjectsActivityFilterCacheKey(
            action.meta.arg?.activeEmployerInfo
          );

          setVerottajailmoitettavaSourceStatus(
            state,
            cacheKey,
            sourceKey,
            'rejected'
          );
        });
    });

    builder.addCase(unsentFilingsOfProjectChanged, (state, action) => {
      const { cacheKey, projectId, hasUnsentFilings } = action.payload || {};
      const sourceKey = VEROTTAJAILMOITETTAVA_SOURCE_KEYS.unsentFilings;

      if (!cacheKey || !projectId) {
        return;
      }

      const projectIds =
        state.verottajailmoitettavaProjectIdsByEmployer[cacheKey]?.[sourceKey];

      if (hasUnsentFilings === false && projectIds?.includes(projectId)) {
        state.verottajailmoitettavaProjectIdsByEmployer[cacheKey][sourceKey] =
          projectIds.filter((id) => id !== projectId);
      }

      // The API stays the judge. A loaded source goes stale, so the next load
      // asks again (the source thunks refetch whatever is not fulfilled), and
      // so does a source still loading: its answer can predate this change.
      // Not asked right away, as the filing index the API reads is eventually
      // consistent; by the time the list is on screen again it has caught up.
      const status = getVerottajailmoitettavaSourceStatus(
        state,
        cacheKey,
        sourceKey
      );

      if (status === 'fulfilled' || status === 'pending') {
        setVerottajailmoitettavaSourceStatus(
          state,
          cacheKey,
          sourceKey,
          'stale'
        );
      }
    });

    builder
      .addCase(setMobileShowList, (state, action) => {
        state.mobileShowList = action.payload;
      })
      .addCase(setSelectedProjectId, (state, action) => {
        state.selectedProjectId = action.payload;
      })
      .addCase(setSelectedFolder, (state, action) => {
        state.folder = action.payload;
      })
      .addCase(toggleShowAll, (state, action) => {
        state.projectsActivityFilterMode =
          state.projectsActivityFilterMode ===
          PROJECTS_ACTIVITY_FILTER_MODES.ALL
            ? PROJECTS_ACTIVITY_FILTER_MODES.LATEST
            : PROJECTS_ACTIVITY_FILTER_MODES.ALL;
        state.showAll =
          state.projectsActivityFilterMode ===
          PROJECTS_ACTIVITY_FILTER_MODES.ALL;
      })
      .addCase(setProjectsActivityFilterMode, (state, action) => {
        state.projectsActivityFilterMode = action.payload;
        state.showAll = action.payload === PROJECTS_ACTIVITY_FILTER_MODES.ALL;
      })
      .addCase(toggleShowShortTermProjects, (state, action) => {
        state.showShortTermProjects = !state.showShortTermProjects;
      })
      .addCase(setSelectedShortTermProjectsViewMode, (state, action) => {
        state.selectedShortTermProjectsViewMode = action.payload;
      })
      .addCase(loadUserProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUserProjects.fulfilled, (state, action) => {
        const userProjects = action.payload.filter(
          (project) => project && project.id
        );

        state.projects = projectsAdapter.setMany(state.projects, userProjects);
        state.loading = false;
        state.userProjectsLoaded = true;
      })
      .addCase(loadUserProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(loadAllBusinessIdProjects.pending, (state) => {
        state.businessIdProjectsLoading = true;
      })
      .addCase(loadAllBusinessIdProjects.fulfilled, (state, action) => {
        const projectsOfBusiness = action.payload.filter(
          (project) => project && project.id
        );

        state.projectsOfBusiness = projectsAdapter.setMany(
          state.projectsOfBusiness,
          projectsOfBusiness
        );
        state.businessIdProjectsLoading = false;
      })
      .addCase(loadAllBusinessIdProjects.rejected, (state, action) => {
        state.businessIdProjectsLoading = false;
        state.error = action.error;
      })
      .addCase(loadUserProjectsAndResources.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUserProjectsAndResources.fulfilled, (state, action) => {
        state.projects = projectsAdapter.setMany(
          state.projects,
          action.payload.projects
        );
        state.projectToRedirectTo = action.payload.projectToRedirectTo;
        state.loading = false;
        state.userProjectsLoaded = true;
      })
      .addCase(loadUserProjectsAndResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(loadProjectByProjectKey.fulfilled, (state, action) => {
        state.projects = projectsAdapter.setOne(state.projects, action.payload);
      })
      .addCase(
        loadProjectByProjectKeyAfterJoining.fulfilled,
        (state, action) => {
          state.projects = projectsAdapter.setOne(
            state.projects,
            action.payload
          );
        }
      )
      .addCase(addEmployerForUser.pending, (state, action) => {
        state.isAddingEmployer = true;
      })
      .addCase(addEmployerForUser.fulfilled, (state, action) => {
        state.projects = projectsAdapter.setOne(
          state.projects,
          action.payload.newEmployerProject
        );
        state.isAddingEmployer = false;
      })
      .addCase(userJoinProject.fulfilled, (state, action) => {
        state.projects = projectsAdapter.setMany(
          state.projects,
          action.payload.projects
        );
      })
      .addCase(addEmployerForUser.rejected, (state, action) => {
        state.isAddingEmployer = false;
      })
      .addCase(loadLatestActiveProjectsOfUser.pending, (state, action) => {
        const cacheKey = getProjectsActivityFilterCacheKey(
          action.meta.arg?.activeEmployerInfo
        );

        if (!cacheKey) {
          return;
        }

        state.latestUserActiveProjectIdsRequestStatusByEmployer[cacheKey] =
          'pending';
      })
      .addCase(loadLatestActiveProjectsOfUser.fulfilled, (state, action) => {
        state.latestUserActiveProjectIdsByEmployer[action.payload.cacheKey] =
          action.payload.projectIds;
        state.latestUserRecordProjectIdsByEmployer[action.payload.cacheKey] =
          action.payload.recordProjectIds || [];
        state.latestUserActiveProjectIdsRequestStatusByEmployer[
          action.payload.cacheKey
        ] = 'fulfilled';
      })
      .addCase(loadLatestActiveProjectsOfUser.rejected, (state, action) => {
        const cacheKey = getProjectsActivityFilterCacheKey(
          action.meta.arg?.activeEmployerInfo
        );

        if (!cacheKey) {
          return;
        }

        state.latestUserActiveProjectIdsRequestStatusByEmployer[cacheKey] =
          'rejected';
      })
      .addCase(loadUserProjectPreferences.pending, (state) => {
        state.userProjectPreferencesLoading = true;
      })
      .addCase(loadUserProjectPreferences.fulfilled, (state, action) => {
        const preferencesByKey = {};

        (action.payload || []).forEach((preference) => {
          if (!preference?.projectId || !preference?.businessId) {
            return;
          }

          preferencesByKey[getUserProjectPreferenceKey(preference)] = preference;
        });

        state.userProjectPreferences = preferencesByKey;
        state.userProjectPreferencesLoading = false;
        state.userProjectPreferencesLoaded = true;
      })
      .addCase(loadUserProjectPreferences.rejected, (state, action) => {
        state.userProjectPreferencesLoading = false;
        state.error = action.error;
      })
      .addCase(
        applyOptimisticUserProjectPreferenceUpdate,
        (state, action) => {
          const {
            requestId,
            cacheKey,
            previousPreference,
            previousLatestProjectIds,
            preference,
          } = action.payload || {};

          if (!requestId || !preference?.projectId || !preference?.businessId) {
            return;
          }

          const preferenceKey = getUserProjectPreferenceKey(preference);
          state.userProjectPreferenceUpdatesByRequestId[requestId] = {
            cacheKey,
            preferenceKey,
            previousPreference,
            previousLatestProjectIds,
          };
          state.latestUserProjectPreferenceRequestIdByPreferenceKey[
            preferenceKey
          ] = requestId;
          state.userProjectPreferences[preferenceKey] = preference;

          if (preference.favorite && cacheKey) {
            state.latestUserActiveProjectIdsByEmployer[cacheKey] =
              moveProjectIdToFront(
                state.latestUserActiveProjectIdsByEmployer[cacheKey] || [],
                preference.projectId
              );
          }
        }
      )
      .addCase(updateUserProjectPreference.fulfilled, (state, action) => {
        const preferenceKey = getUserProjectPreferenceKey(action.payload);
        const latestRequestId =
          state.latestUserProjectPreferenceRequestIdByPreferenceKey[
            preferenceKey
          ];

        delete state.userProjectPreferenceUpdatesByRequestId[
          action.meta.requestId
        ];

        if (!action.payload?.projectId || !action.payload?.businessId) {
          return;
        }

        if (latestRequestId && latestRequestId !== action.meta.requestId) {
          return;
        }

        delete state.latestUserProjectPreferenceRequestIdByPreferenceKey[
          preferenceKey
        ];
        state.userProjectPreferences[preferenceKey] = action.payload;
      })
      .addCase(updateUserProjectPreference.rejected, (state, action) => {
        const requestId = action.meta.requestId;
        const snapshot = state.userProjectPreferenceUpdatesByRequestId[requestId];
        const latestRequestId =
          state.latestUserProjectPreferenceRequestIdByPreferenceKey[
            snapshot?.preferenceKey
          ];

        if (!snapshot) {
          return;
        }

        delete state.userProjectPreferenceUpdatesByRequestId[requestId];

        if (latestRequestId && latestRequestId !== requestId) {
          return;
        }

        if (snapshot.previousPreference) {
          state.userProjectPreferences[snapshot.preferenceKey] =
            snapshot.previousPreference;
        } else {
          delete state.userProjectPreferences[snapshot.preferenceKey];
        }

        if (snapshot.cacheKey) {
          state.latestUserActiveProjectIdsByEmployer[snapshot.cacheKey] =
            snapshot.previousLatestProjectIds || [];
        }

        delete state.latestUserProjectPreferenceRequestIdByPreferenceKey[
          snapshot.preferenceKey
        ];
      })
      .addCase(loadProjectRecordsFromToday.fulfilled, (state, action) => {
        state.projectEmployeeEmployerProjects =
          projectEmployeeEmployerProjectsAdapter.setMany(
            state.projectEmployeeEmployerProjects,
            action.payload.employerProjectsOfProjectResources
          );
      })
      .addCase(createProject.pending, (state, action) => {
        state.isProjectCreateLoading = true;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        projectsAdapter.setOne(state.projects, action.payload.newProject);
        state.isProjectCreateLoading = false;
      })

      .addCase(createProject.rejected, (state, action) => {
        state.isProjectCreateLoading = false;
      })
      .addCase(updateProject.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        const id = action.payload.id;
        const existing = state.projects.entities[id];
        if (existing) {
          state.projects.entities[id] = { ...action.payload };
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.error = action.error;
      })
      .addCase(deleteProject.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = projectsAdapter.updateOne(state.projects, {
          id: action.payload.updatedProject.id,
          changes: action.payload.updatedProject,
        });
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.error = action.error;
      })
      .addCase(createShortTermProject.pending, (state, action) => {
        state.isProjectCreateLoading = true;
      })
      .addCase(createShortTermProject.fulfilled, (state, action) => {
        projectsAdapter.setOne(state.projects, action.payload.newProject);
        state.isProjectCreateLoading = false;
      })
      .addCase(createShortTermProject.rejected, (state, action) => {
        state.isProjectCreateLoading = false;
        enqueueSnackbar('Tilauksen luonti epäonnistui', { variant: 'error' });
      })
      .addCase(updateProjectStatus.pending, (state, action) => {
        state.isUpdatingProjectStatus = true;
      })
      .addCase(updateProjectStatus.fulfilled, (state, action) => {
        projectsAdapter.setOne(state.projects, action.payload);
        enqueueSnackbar('Projektin tila päivitetty', { variant: 'success' });
        state.isUpdatingProjectStatus = false;
      })
      .addCase(updateProjectStatus.rejected, (state, action) => {
        enqueueSnackbar('Projektin tilan päivitys epäonnistui', {
          variant: 'error',
        });
        state.isUpdatingProjectStatus = false;
      })

      .addCase(loadBusinessShortTermProjects.pending, (state, action) => {
        state.isLoadingShortTermProjects = true;
      })
      .addCase(loadBusinessShortTermProjects.fulfilled, (state, action) => {
        projectsAdapter.setMany(state.projects, action.payload);
        state.isLoadingShortTermProjects = false;
      })
      .addCase(loadBusinessShortTermProjects.rejected, (state, action) => {
        state.isLoadingShortTermProjects = false;
      })
      .addCase(loadArchivedProjectsOfBusiness.pending, (state, action) => {
        state.isLoadingArchivedProjects = true;
      })
      .addCase(loadArchivedProjectsOfBusiness.fulfilled, (state, action) => {
        projectsAdapter.setMany(state.projects, action.payload);
        state.isLoadingArchivedProjects = false;
      })
      .addCase(loadArchivedProjectsOfBusiness.rejected, (state, action) => {
        state.isLoadingArchivedProjects = false;
      })
      .addCase(updateProjectDatesFromFilings.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProjectDatesFromFilings.fulfilled, (state, action) => {
        state.projects = projectsAdapter.updateOne(state.projects, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      .addCase(updateProjectDatesFromFilings.rejected, (state, action) => {
        state.error = action.error;
      })
      .addCase(createOrUpdateUser.fulfilled, (state, action) => {
        if (action.payload.employer) {
          projectsAdapter.setOne(state.projects, action.payload.employer);
        }
      })
      .addCase(updateProjectIdCard.fulfilled, (state, action) => {
        state.projects = projectsAdapter.upsertOne(
          state.projects,
          action.payload
        );
      })
      .addCase(saveProjectDashboardLayout.fulfilled, (state, action) => {
        const { projectId, dashboardLayout, updatedAt } = action.payload;

        state.projects = projectsAdapter.updateOne(state.projects, {
          id: projectId,
          // updatedAt only when the API gave one, so a missing one does not
          // blank the project's.
          changes: { dashboardLayout, ...(updatedAt && { updatedAt }) },
        });
      })

      .addCase(updateProjectFromIlve.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProjectFromIlve.fulfilled, (state, action) => {
        state.projects = projectsAdapter.upsertOne(
          state.projects,
          action.payload.project
        );
      })
      .addCase(updateProjectFromIlve.rejected, (state, action) => {
        state.error = action.error;
      })

      .addCase(loadProjectRecordTotalHours.pending, (state) => {
        state.projectRecordHoursLoading = true;
      })
      .addCase(loadProjectRecordTotalHours.fulfilled, (state, action) => {
        state.projectRecordHours[action.payload.id] =
          action.payload.recordHourData;
        state.projectRecordHoursLoading = false;
      })
      .addCase(loadProjectRecordTotalHours.rejected, (state) => {
        state.projectRecordHoursLoading = false;
      })
      .addCase(loadProcountorDimensions.fulfilled, (state, action) => {
        state.procountorDimensions = action.payload.procountorDimensions;
      })
      .addCase(saveProcountorDimension.pending, (state) => {
        state.savingProcountorDimension = true;
      })
      .addCase(saveProcountorDimension.fulfilled, (state, action) => {
        state.savingProcountorDimension = false;
        projectsAdapter.upsertOne(state.projects, {
          id: action.payload.projectId,
          procountorProjectDimensionId:
            action.payload.procountorProjectDimensionId,
        });
      })
      .addCase(saveProcountorDimension.rejected, (state, action) => {
        state.savingProcountorDimension = false;
      })
      .addCase(setSearchFilter, (state, action) => {
        state.searchFilter = action.payload;
      })
      .addCase(
        loadResourcesAndResourceEmployersOfProject.fulfilled,
        (state, action) => {
          if (action.payload.employers) {
            const employers = action.payload.employers.filter(
              (employer) => employer && employer.id
            );
            state.projects = projectsAdapter.setMany(state.projects, employers);
          }
        }
      )
      .addCase(putProjectToMergeInto.fulfilled, (state, action) => {
        projectsAdapter.setOne(state.projects, action.payload.updatedProject);
        state.projectMergeStatus.mergeIntoStatus =
          action.payload.mergeIntoStatus;
      })
      .addCase(addProjectToMergeFrom.fulfilled, (state, action) => {
        projectsAdapter.setOne(state.projects, action.payload.updatedProject);
        state.projectMergeStatus.mergeFromStatus =
          state.projectMergeStatus.mergeFromStatus.filter(
            (status) =>
              status.projectKey !== action.payload.mergeFromStatus.projectKey
          );
        state.projectMergeStatus.mergeFromStatus.push(
          action.payload.mergeFromStatus
        );
      })
      .addCase(getProjectMergeStatus.fulfilled, (state, action) => {
        state.projectMergeStatus = action.payload;
      })
      .addCase(removeProjectToMergeFrom.fulfilled, (state, action) => {
        state.projectMergeStatus.mergeFromStatus =
          state.projectMergeStatus.mergeFromStatus.filter(
            (status) => status.projectKey !== action.payload.removedKey
          );
      })
      .addCase(startProjectMergeFrom.fulfilled, (state, action) => {
        state.projectMergeStatus.mergeFromStatus =
          state.projectMergeStatus.mergeFromStatus.filter(
            (status) => status.projectKey !== action.meta.arg
          );
      })
      .addCase(
        loadResourcesOfProjectWithEmployerData.fulfilled,
        (state, action) => {
          projectsAdapter.setMany(
            state.projects,
            action.payload.employerProjects
          );
        }
      )
      .addCase(
        loadResourcesDataOfProjectSpecificFeature.fulfilled,
        (state, action) => {
          projectsAdapter.setMany(
            state.projects,
            action.payload.employerProjects
          );
        }
      )
      .addCase(loadBusinessIntegrationPartners.fulfilled, (state, action) => {
        if (action.payload) {
          const { businessId, integrationPartners } = action.payload;

          state.businessIntegrationPartners = {
            ...state.businessIntegrationPartners,
            [businessId]: integrationPartners,
          };
        }
      })
      .addCase(
        loadBusinessIntegrationPartnersForAnyProject.fulfilled,
        (state, action) => {
          if (action.payload) {
            const { businessId, integrationPartners } = action.payload;

            state.businessIntegrationPartners = {
              ...state.businessIntegrationPartners,
              [businessId]: integrationPartners,
            };
          }
        }
      )
      .addCase(setShortTermProjectSearchFilterValue, (state, action) => {
        state.shortTermProjectsSearchFilters = {
          ...state.shortTermProjectsSearchFilters,
          ...action.payload,
        };
      })
      .addCase(closeShortTermProjectsView, (state, action) => {
        state.shortTermProjectsSearchFilters =
          initialShortTermProjectsSearchFilters;
      })
      .addCase(clearProjectsOfBusiness, (state) => {
        state.projectsOfBusiness = projectsOfBusinessAdapter.getInitialState();
      })
      .addCase(setProjectActivityFromDate, (state, action) => {
        state.projectActivityFromDate = action.payload;
      })
      .addCase(setProjectActivityToDate, (state, action) => {
        state.projectActivityToDate = action.payload;
      })
      .addCase(
        loadProjectsActivityInformationIntimeframe.pending,
        (state, action) => {
          state.isLoadingProjectActivity = true;
          state.projectActivityData = {};
        }
      )
      .addCase(
        loadProjectsActivityInformationIntimeframe.fulfilled,
        (state, action) => {
          state.projectActivityData = action.payload;
          state.isLoadingProjectActivity = false;
        }
      )
      .addCase(
        loadProjectsActivityInformationIntimeframe.rejected,
        (state, action) => {
          state.isLoadingProjectActivity = false;
        }
      )
      .addCase(
        loadProjectFeatureStatusIndicatorItems.fulfilled,
        (state, action) => {
          state.projectFeatureStatusItems = action.payload;
        }
      )
      .addCase(setSelectedStatusBasedProjectsMode, (state, action) => {
        state.selectedStatusBasedProjectsMode = action.payload;
      })
      .addMatcher(
        isAnyOf(startProjectMergeFrom.pending, startProjectMergeInto.pending),
        (state, action) => {
          state.projectsMerging = true;
        }
      )
      .addMatcher(
        isAnyOf(
          startProjectMergeFrom.fulfilled,
          startProjectMergeFrom.rejected,
          startProjectMergeInto.fulfilled,
          startProjectMergeInto.rejected
        ),
        (state, action) => {
          state.projectsMerging = false;
        }
      )

      .addMatcher(
        isAnyOf(
          removeProjectToMergeInto.fulfilled,
          startProjectMergeInto.fulfilled
        ),
        (state, action) => {
          state.projectMergeStatus.mergeIntoStatus = undefined;
        }
      )
      .addMatcher(
        isAnyOf(addProjectToMergeFrom.pending, putProjectToMergeInto.pending),
        (state, action) => {
          state.addingProjectToMerge = true;
        }
      )
      .addMatcher(
        isAnyOf(
          addProjectToMergeFrom.fulfilled,
          addProjectToMergeFrom.rejected,
          putProjectToMergeInto.fulfilled,
          putProjectToMergeInto.rejected
        ),
        (state, action) => {
          state.addingProjectToMerge = false;
        }
      );
  },
});

export const {
  setShowHiddenProjects,
  toggleShowHiddenProjects,
  setShowArchivedProjects,
  toggleShowArchivedProjects,
  setShowEndedProjects,
  toggleShowEndedProjects,
  addProjectToLatestActiveProjects,
  setLatestProjectsListSourceMode,
  setVerottajailmoitettavaListMode,
  setTimerDrawerSourceMode,
  setProjectEditDialogTab,
} = projectsSlice.actions;

export const {
  selectAll: selectAllProjectsOfBusiness,
  selectEntities: selectProjectsOfBusinessEntities,
  selectById: selectProjectOfBusinessById,
  selectIds: selectProjectsOfBusinessIds,
} = projectsOfBusinessAdapter.getSelectors(
  (state) => state.projects.projectsOfBusiness
);

export default projectsSlice.reducer;
